import { fetcher } from '@commenda/utils';

import type {
  IIndirectTaxUpdateRegistrationCalculationSettingParams,
  IIndirectTaxUpdateRegistrationCalculationSettingResult,
} from './types';

/**
 * Updates the `disable_tax_calculation` flag on the corporation's jurisdiction
 * settings (CJC) for a single (country, state). Preserves the other JSONB
 * fields (`manage_filings`, `disable_exemptions_after_expiration`) verbatim
 * because the backend REPLACES the JSONB blob on every write.
 *
 * Hits the existing sales-tax settings endpoint — there is no indirect-tax-
 * native settings route today. Consumers in indirect-tax UI should read the
 * existing values first and pass them through as `currentManageFilings` /
 * `currentExemptionExpiration` to avoid clobbering.
 */
export async function updateIndirectTaxRegistrationCalculationSetting({
  corporationId,
  country,
  state,
  disableTaxCalculation,
  currentManageFilings,
  currentExemptionExpiration,
}: Readonly<IIndirectTaxUpdateRegistrationCalculationSettingParams>): Promise<IIndirectTaxUpdateRegistrationCalculationSettingResult> {
  const jurisdictionId = `${country}_${state}`;

  const body = {
    configuration: {
      type: 'SALES_TAX_SETTINGS',
      data: {
        disable_tax_calculation: disableTaxCalculation,
        manage_filings: currentManageFilings,
        ...(currentExemptionExpiration
          ? { disable_exemptions_after_expiration: currentExemptionExpiration }
          : {}),
      },
    },
  };

  try {
    const response = await fetcher(
      `new-sales-tax/corporation-filing-setting/${corporationId}/settings/${jurisdictionId}`,
      { method: 'POST', body },
      undefined
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.message || 'Failed to update tax calculation setting',
      };
    }

    return { success: true };
  } catch (error) {
    const errorMessage =
      error instanceof Error
        ? error.message
        : 'Failed to update tax calculation setting';
    return {
      success: false,
      error: errorMessage,
    };
  }
}
